import { useState, useEffect } from "react";
import { Menu, X, User, ChevronDown, Globe } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "./Logo";
import AuthModal from "./AuthModal";
import MerchantEnquiryModal from "./MerchantEnquiryModal";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/hooks/useLanguage";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [enquiryModalOpen, setEnquiryModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, signOut } = useAuth();
  const { language, setLanguage, t, isRTL } = useLanguage();
  const [showArabicBanner, setShowArabicBanner] = useState(false);

  useEffect(() => {
    if (language === 'ar') {
      const dismissed = sessionStorage.getItem('flexaura_ar_banner_dismissed');
      setShowArabicBanner(!dismissed);
    } else {
      setShowArabicBanner(false);
    }
  }, [language]);

  const dismissArabicBanner = () => {
    setShowArabicBanner(false);
    sessionStorage.setItem('flexaura_ar_banner_dismissed', 'true');
  };

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/', { replace: true });
      setTimeout(() => {
        const section = document.getElementById(sectionId) || document.querySelector(`[data-section="${sectionId}"]`);
        if (section) section.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const section = document.getElementById(sectionId) || document.querySelector(`[data-section="${sectionId}"]`);
      if (section) section.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  const handleComingSoonClick = (vertical: string) => {
    toast(`${vertical} financing coming soon. Join the waitlist.`);
  };

  const navLinks = [
    { label: t.nav.travel, href: "/travel", type: "marketplace", weight: "font-semibold", isLive: true },
    { label: t.nav.education, type: "marketplace", weight: "font-semibold", isComingSoon: true, action: () => handleComingSoonClick("Education") },
    { label: t.nav.healthcare, type: "marketplace", weight: "font-semibold", isComingSoon: true, action: () => handleComingSoonClick("Healthcare") },
    { label: t.nav.howItWorks, action: () => scrollToSection("how-it-works"), type: "info", weight: "font-normal" },
    { label: t.nav.partners, href: "/partners", type: "info", weight: "font-normal", isPartners: true },
    { label: t.nav.faq, href: "/faq", type: "info", weight: "font-normal" },
    { label: t.nav.contact, href: "/contact", type: "contact", weight: "font-normal" },
  ];

  const handleAuthAction = () => {
    if (isAuthenticated) {
      navigate('/account/dashboard');
    } else {
      setAuthModalOpen(true);
    }
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ar' : 'en');
  };

  const LanguageToggle = ({ className = "" }: { className?: string }) => (
    <button
      onClick={toggleLanguage}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-primary/30 rounded-full text-foreground hover:bg-primary/5 hover:border-primary/50 transition-colors ${className}`}
      aria-label={language === 'en' ? 'Switch to Arabic' : 'Switch to English'}
    >
      <Globe className="w-3.5 h-3.5" />
      {language === 'en' ? 'العربية' : 'English'}
    </button>
  );

  return (
    <>
      <nav 
        className="fixed top-0 left-0 right-0 z-50 bg-background backdrop-blur-md border-b border-border shadow-sm"
        role="navigation"
        aria-label="Main navigation"
      >
        <a 
          href="#main-content" 
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-primary text-primary-foreground px-4 py-2 rounded-md z-[60]"
        >
          Skip to main content
        </a>
        
        <div className={`max-w-6xl mx-auto px-4 py-3 flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={`flex-shrink-0 ${isRTL ? 'ml-8' : 'mr-8'}`}>
            <Logo />
          </div>
          
          {/* Desktop Navigation */}
          <div className={`hidden md:flex items-center ${isRTL ? 'space-x-reverse space-x-8' : 'space-x-8'}`}>
            {navLinks.map((link) => {
              const baseClasses = `font-body ${link.weight} smooth-transition focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg px-3 py-2`;
              const colorClasses = "text-foreground hover:text-primary";
              
              if (link.isPartners) {
                return (
                  <div key={link.label} className="relative group">
                    <button className={`${baseClasses} ${colorClasses} inline-flex items-center gap-1`}>
                      {link.label}
                      <ChevronDown className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                    </button>
                    <div className={`absolute top-full ${isRTL ? 'right-0' : 'left-0'} mt-1 w-48 bg-card rounded-lg border border-border shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50`}>
                      <button
                        onClick={() => navigate("/partners")}
                        className="block w-full text-left px-4 py-2.5 text-sm font-body text-foreground hover:bg-secondary/50 rounded-t-lg"
                      >
                        {t.nav.partners}
                      </button>
                      <button
                        onClick={() => setEnquiryModalOpen(true)}
                        className="block w-full text-left px-4 py-2.5 text-sm font-body text-primary hover:bg-secondary/50 rounded-b-lg"
                      >
                        {t.nav.partnerWithUs}
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <div key={link.label}>
                  {link.href ? (
                    link.href.startsWith('mailto:') ? (
                      <a href={link.href} className={`${baseClasses} ${colorClasses}`}>{link.label}</a>
                    ) : (
                      <button onClick={() => navigate(link.href!)} className={`${baseClasses} ${colorClasses} inline-flex items-center gap-1.5`}>
                        {link.label}
                        {(link as any).isLive && (
                          <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: '#C9A84C' }} />
                        )}
                      </button>
                    )
                  ) : (
                    <button onClick={link.action} className={`${baseClasses} ${colorClasses} flex flex-col items-start`}>
                      <span className="inline-flex items-center gap-1.5">{link.label}</span>
                      {(link as any).isComingSoon && (
                        <span className="text-[10px] text-muted-foreground font-normal leading-none mt-0.5">(Coming Soon)</span>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
            <LanguageToggle />
            <Button 
              variant={isAuthenticated ? "outline" : "gradient"}
              size="sm" 
              onClick={handleAuthAction}
              className={isRTL ? 'mr-4' : 'ml-4'}
            >
              {isAuthenticated ? (
                <span className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {t.nav.myAccount}
                </span>
              ) : (
                t.nav.signIn
              )}
            </Button>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" aria-label="Open navigation menu" className="p-2">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side={isRTL ? "left" : "right"} className="w-[280px] bg-background/95 backdrop-blur-md">
                <div className="flex flex-col space-y-6 mt-8">
                  <div className="flex justify-between items-center">
                    <Logo />
                    <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)} aria-label="Close navigation menu">
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                  
                  <div className="flex flex-col space-y-3">
                    {navLinks.map((link) => {
                      const mobileBaseClasses = `text-left text-lg font-body ${link.weight} py-3 px-4 smooth-transition focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg block w-full hover:bg-accent/10`;
                      const mobileColorClasses = "text-foreground hover:text-primary";
                      
                      return (
                        <div key={link.label}>
                          {link.href ? (
                            link.href.startsWith('mailto:') ? (
                              <a href={link.href} className={`${mobileBaseClasses} ${mobileColorClasses}`}>{link.label}</a>
                            ) : (
                              <button onClick={() => { navigate(link.href!); setIsOpen(false); }} className={`${mobileBaseClasses} ${mobileColorClasses} inline-flex items-center gap-2`}>
                                {link.label}
                                {(link as any).isLive && (
                                  <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: '#C9A84C' }} />
                                )}
                              </button>
                            )
                          ) : (
                            <button onClick={() => { link.action?.(); setIsOpen(false); }} className={`${mobileBaseClasses} ${mobileColorClasses}`}>
                              {link.label}
                              {(link as any).isComingSoon && (
                                <span className="text-xs text-muted-foreground font-normal ml-2">(Coming Soon)</span>
                              )}
                            </button>
                          )}
                          {link.isPartners && (
                            <button
                              onClick={() => { setEnquiryModalOpen(true); setIsOpen(false); }}
                              className={`${mobileBaseClasses} text-primary hover:text-primary/80 text-base pl-8`}
                            >
                              {t.nav.partnerWithUs}
                            </button>
                          )}
                        </div>
                      );
                    })}

                    <div className="px-4 pt-2">
                      <LanguageToggle className="w-full justify-center" />
                    </div>
                    
                    <Button 
                      variant={isAuthenticated ? "outline" : "gradient"}
                      onClick={() => { handleAuthAction(); setIsOpen(false); }}
                      className="mt-6 w-full"
                    >
                      {isAuthenticated ? t.nav.myAccount : t.nav.signIn}
                    </Button>

                    {isAuthenticated && (
                      <Button
                        variant="ghost"
                        onClick={() => { signOut(); setIsOpen(false); }}
                        className="w-full text-muted-foreground"
                      >
                        {t.nav.signOut}
                      </Button>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      {showArabicBanner && (
        <div className="fixed top-16 left-0 right-0 z-40 bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-center gap-3" dir="rtl">
          <p className="text-sm text-amber-800 font-body">
            الترجمة العربية قادمة قريباً — بعض الصفحات لا تزال باللغة الإنجليزية حالياً.
          </p>
          <button
            onClick={dismissArabicBanner}
            className="text-amber-600 hover:text-amber-800 transition-colors flex-shrink-0"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />
      <MerchantEnquiryModal open={enquiryModalOpen} onOpenChange={setEnquiryModalOpen} />
    </>
  );
};

export default Navigation;
